import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useEmblaCarousel from 'embla-carousel-react';
import {
  ArrowLeft,
  Calendar,
  Tag,
  ChevronLeft,
  ChevronRight,
  Building2,
  Briefcase,
  UserCircle2,
  Clock,
  CheckCircle2,
  Quote,
} from 'lucide-react';
import { portfolioProjects } from '../data/mock';
import useSEO from '../hooks/useSEO';
import { SITE_URL } from '../config/seo';

gsap.registerPlugin(ScrollTrigger);

const absoluteUrl = (maybe) => {
  if (!maybe) return `${SITE_URL}/favicon.png`;
  if (/^https?:\/\//i.test(maybe)) return maybe;
  return `${SITE_URL}${maybe.startsWith('/') ? '' : '/'}${maybe}`;
};

const WorkDetailSEO = ({ project }) => {
  const canonical = project
    ? `${SITE_URL}/portfolio/${project.id}`
    : `${SITE_URL}/portfolio`;

  const categoryPhrase = project
    ? /^ai\b/i.test(project.category)
      ? project.category
      : `AI ${project.category}`
    : '';

  const seoTitle = project?.seo?.title
    || (project ? `${project.title} — ${categoryPhrase} Case Study | Cultiv AI Studio` : 'Project Not Found | Cultiv AI Studio');

  const seoDescription = project?.seo?.description
    || (project
      ? `${project.description} — an ${categoryPhrase} case study by Cultiv AI Studio.`
      : 'This project could not be found. Explore Cultiv AI Studio\'s portfolio of AI creative work.');

  const keywords = project?.seo?.keywords;
  const ogImage = absoluteUrl(project?.image);

  const jsonLd = project
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'CreativeWork',
          '@id': `${canonical}#creativework`,
          name: project.title,
          headline: project.title,
          description: project.seo?.description || project.description,
          url: canonical,
          image: ogImage,
          genre: project.category,
          keywords: project.seo?.keywords?.join(', '),
          about: project.industry,
          creator: { '@id': `${SITE_URL}/#organization` },
          isPartOf: { '@id': `${SITE_URL}/#website` },
          ...(project.client ? { sourceOrganization: project.client } : {}),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}/portfolio` },
            { '@type': 'ListItem', position: 3, name: project.title, item: canonical },
          ],
        },
      ]
    : undefined;

  useSEO({
    title: seoTitle,
    description: seoDescription,
    keywords,
    ogImage,
    canonical,
    noindex: !project,
    ogType: 'article',
    jsonLd,
  });
  return null;
};

const MetaChip = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/5">
    <Icon size={18} className="text-violet-400 mt-0.5" />
    <div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-1">{label}</div>
      <div className="text-sm text-white/90">{value}</div>
    </div>
  </div>
);

const SectionHeader = ({ eyebrow, title }) => (
  <div className="mb-8">
    {eyebrow && (
      <div className="text-[11px] uppercase tracking-[0.22em] text-violet-400/80 mb-3">{eyebrow}</div>
    )}
    <h2 className="text-3xl md:text-4xl font-light tracking-tight">{title}</h2>
  </div>
);

const WorkDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const project = portfolioProjects.find(p => p.id === parseInt(id));

  const slides = project ? (project.images?.length ? project.images : (project.image ? [project.image] : [])) : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', duration: 22, skipSnaps: false });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideErrors, setSlideErrors] = useState({});
  const onSelect = useCallback(() => {
    if (emblaApi) setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);
  useEffect(() => {
    if (!emblaApi || slides.length <= 1) return;
    const t = setInterval(() => emblaApi.scrollNext(), 4000);
    return () => clearInterval(t);
  }, [emblaApi, slides.length]);
  const scrollPrev = (e) => { e.stopPropagation(); emblaApi?.scrollPrev(); };
  const scrollNext = (e) => { e.stopPropagation(); emblaApi?.scrollNext(); };
  const scrollTo = (e, i) => { e.stopPropagation(); emblaApi?.scrollTo(i); };
  const handleSlideError = (i) => setSlideErrors((prev) => ({ ...prev, [i]: true }));
  const showSliderControls = slides.length > 1;

  const relatedProjects = useMemo(() => {
    if (!project?.relatedProjectIds?.length) return [];
    return project.relatedProjectIds
      .map((rid) => portfolioProjects.find((p) => p.id === rid))
      .filter(Boolean);
  }, [project]);

  useEffect(() => {
    setSlideErrors({});
    setSelectedIndex(0);
  }, [id]);

  useEffect(() => {
    gsap.fromTo(
      '.detail-section',
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 1.5,
        stagger: 0.15,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: '.detail-container',
          start: 'top 85%',
          end: 'top 30%',
          scrub: 0.3,
          toggleActions: 'play none none reverse'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  if (!project) {
    return (
      <>
        <WorkDetailSEO project={null} />
        <div className="bg-black text-white min-h-screen pt-32 pb-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-light mb-4">Project Not Found</h1>
            <button
              onClick={() => navigate('/portfolio')}
              className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
              Back to Portfolio
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
    <WorkDetailSEO project={project} />
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 detail-container">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/50">
          <ol className="flex items-center gap-2">
            <li><Link to="/" className="hover:text-white/80">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/portfolio" className="hover:text-white/80">Portfolio</Link></li>
            <li aria-hidden>/</li>
            <li className="text-white/80" aria-current="page">{project.title}</li>
          </ol>
        </nav>

        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          onClick={() => navigate('/portfolio')}
          className="inline-flex items-center gap-2 mb-10 text-white/60 hover:text-white transition-colors group"
        >
          <ArrowLeft size={20} className="transition-transform group-hover:-translate-x-1" />
          Back to Portfolio
        </motion.button>

        {/* Project Header */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-12 detail-section"
        >
          <h1 className="text-5xl md:text-7xl font-light tracking-tight mb-6">
            {project.title}
          </h1>
          <div className="flex flex-wrap items-center gap-6 text-white/60 mb-8">
            <div className="flex items-center gap-2">
              <Tag size={16} />
              <span className="text-sm">{project.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              <span className="text-sm">{project.year || '2024'}</span>
            </div>
          </div>
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl">
            {project.description}
          </p>
        </motion.header>

        {/* Engagement meta */}
        {(project.client || project.industry || project.role || project.duration) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16 detail-section"
          >
            {project.client && <MetaChip icon={Building2} label="Client" value={project.client} />}
            {project.industry && <MetaChip icon={Briefcase} label="Industry" value={project.industry} />}
            {project.role && <MetaChip icon={UserCircle2} label="Role" value={project.role} />}
            {project.duration && <MetaChip icon={Clock} label="Duration" value={project.duration} />}
          </motion.div>
        )}

        {/* Project Image Slider */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-20 detail-section"
        >
          <div className="aspect-video bg-gradient-to-br from-violet-900/20 via-purple-900/10 to-blue-900/20 rounded-2xl overflow-hidden relative border border-violet-500/20 neon-border neon-border-hover">
            {slides.length > 0 ? (
              <>
                <div className="absolute inset-0 rounded-2xl overflow-hidden" ref={emblaRef}>
                  <div className="flex h-full">
                    {slides.map((src, i) => (
                      <div key={i} className="flex-[0_0_100%] min-w-0 h-full relative">
                        {slideErrors[i] ? (
                          <div className="absolute inset-0 bg-gradient-to-br from-violet-900/25 via-purple-900/15 to-blue-900/25 flex items-center justify-center" />
                        ) : (
                          <img
                            src={src}
                            alt={`${project.title} – ${i + 1}`}
                            width={1600}
                            height={900}
                            className="w-full h-full object-cover object-center block"
                            loading={i === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            fetchPriority={i === 0 ? 'high' : 'auto'}
                            onError={() => handleSlideError(i)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {showSliderControls && (
                  <>
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent rounded-b-2xl pointer-events-none" aria-hidden />
                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5 pointer-events-auto z-10">
                      {slides.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          aria-label={`Go to slide ${i + 1}`}
                          onClick={(e) => scrollTo(e, i)}
                          className="h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                          style={{
                            width: selectedIndex === i ? 20 : 8,
                            backgroundColor: selectedIndex === i ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      aria-label="Previous"
                      onClick={scrollPrev}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-white/90 flex items-center justify-center hover:bg-black/70 transition-all z-10"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next"
                      onClick={scrollNext}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-white/90 flex items-center justify-center hover:bg-black/70 transition-all z-10"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="w-64 h-64 border-2 border-violet-500/30 rounded-3xl"
                  style={{ boxShadow: '0 0 60px rgba(139, 92, 246, 0.3), inset 0 0 60px rgba(139, 92, 246, 0.1)' }}
                />
                <motion.div
                  animate={{ rotate: [360, 0], scale: [1, 1.3, 1] }}
                  transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
                  className="absolute w-48 h-48 border-2 border-blue-500/30 rounded-full"
                  style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.3), inset 0 0 40px rgba(59, 130, 246, 0.1)' }}
                />
              </div>
            )}
          </div>
        </motion.div>

        {/* Problem */}
        {project.problem && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="01 — The problem" title="What the client was up against" />
            <div className="max-w-3xl">
              <p className="text-white/75 text-lg leading-relaxed whitespace-pre-line">{project.problem}</p>
            </div>
          </motion.section>
        )}

        {/* Approach */}
        {project.approach && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="02 — Our approach" title="How Cultiv AI Studio solved it" />
            <div className="max-w-3xl">
              <p className="text-white/75 text-lg leading-relaxed whitespace-pre-line">{project.approach}</p>
            </div>
          </motion.section>
        )}

        {/* Process */}
        {Array.isArray(project.process) && project.process.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="03 — Process" title="Our working steps" />
            <ol className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {project.process.map((step, idx) => (
                <li
                  key={`${project.id}-step-${step.step || idx}`}
                  className="p-6 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-900/10 to-transparent neon-border"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-9 h-9 rounded-full border border-violet-500/40 bg-violet-500/10 flex items-center justify-center text-violet-300 text-sm">
                      {String(step.step ?? idx + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-lg text-white">{step.title}</h3>
                  </div>
                  <p className="text-white/65 text-sm leading-relaxed">{step.description}</p>
                </li>
              ))}
            </ol>
          </motion.section>
        )}

        {/* Deliverables + Tools */}
        {(Array.isArray(project.deliverables) && project.deliverables.length) ||
        (Array.isArray(project.tools) && project.tools.length) ? (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Array.isArray(project.deliverables) && project.deliverables.length > 0 && (
                <div className="p-8 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-900/10 to-transparent neon-border">
                  <h3 className="text-[11px] uppercase tracking-[0.22em] text-violet-400/80 mb-3">04 — Deliverables</h3>
                  <h4 className="text-2xl font-light mb-6">What we shipped</h4>
                  <ul className="space-y-3">
                    {project.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-white/75 text-sm leading-relaxed">
                        <CheckCircle2 size={16} className="text-violet-400 mt-0.5 shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(project.tools) && project.tools.length > 0 && (
                <div className="p-8 rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-900/10 to-transparent neon-border">
                  <h3 className="text-[11px] uppercase tracking-[0.22em] text-blue-400/80 mb-3">05 — Tools &amp; tech</h3>
                  <h4 className="text-2xl font-light mb-6">What powered the work</h4>
                  <div className="flex flex-wrap gap-2">
                    {project.tools.map((t) => (
                      <span
                        key={t}
                        className="px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-200"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  {Array.isArray(project.technologies) && project.technologies.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <div className="text-[11px] uppercase tracking-[0.18em] text-white/40 mb-3">Capabilities</div>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((t) => (
                          <span
                            key={t}
                            className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white/70"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.section>
        ) : null}

        {/* Results */}
        {Array.isArray(project.results) && project.results.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="06 — Results" title="What actually happened" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {project.results.map((r, idx) => (
                <div
                  key={`${project.id}-result-${idx}`}
                  className="p-8 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent"
                >
                  <div className="text-3xl md:text-4xl font-light text-white mb-2">{r.value}</div>
                  <div className="text-sm text-violet-300 mb-3">{r.label}</div>
                  {r.detail && (
                    <div className="text-white/60 text-sm leading-relaxed">{r.detail}</div>
                  )}
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Extended overview */}
        {project.extendedOverview && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="07 — The full story" title="Extended case study" />
            <div className="max-w-3xl space-y-5 text-white/75 leading-relaxed">
              {project.extendedOverview
                .split(/\n+/)
                .filter(Boolean)
                .map((para, i) => (
                  <p key={i} className="text-base md:text-lg">{para}</p>
                ))}
              <p className="text-sm text-white/60 pt-2">
                More from this series lives in our{' '}
                <Link
                  to="/ai-creatives"
                  title="Browse the AI creatives gallery"
                  className="text-violet-300 hover:text-white underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
                >
                  AI creatives gallery
                </Link>
                , alongside campaign reels and brand experiments.
              </p>
            </div>
          </motion.section>
        )}

        {/* Testimonial */}
        {project.testimonialQuote && (
          <motion.figure
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <div className="p-10 rounded-2xl border border-violet-500/20 bg-gradient-to-br from-violet-900/15 to-transparent neon-border">
              <Quote size={28} className="text-violet-400 mb-5" />
              <blockquote className="text-xl md:text-2xl font-light text-white/90 leading-relaxed mb-6">
                &ldquo;{project.testimonialQuote}&rdquo;
              </blockquote>
              {project.testimonialAuthor && (
                <figcaption className="text-sm text-white/60">
                  — {project.testimonialAuthor}
                </figcaption>
              )}
            </div>
          </motion.figure>
        )}

        {/* Related work */}
        {relatedProjects.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-20 detail-section"
          >
            <SectionHeader eyebrow="Explore more" title="Related work" />
            <p className="text-white/60 text-sm md:text-base max-w-2xl mb-8">
              Keep exploring our AI creative work — here are more case studies that pair well with{' '}
              <span className="text-white/80">{project.title}</span>, hand-picked because they share
              design language, tooling, or business outcome.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedProjects.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/portfolio/${rp.id}`}
                  className="group p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex gap-5 items-center"
                >
                  {rp.image && (
                    <img
                      src={rp.image}
                      alt={rp.title}
                      width={96}
                      height={96}
                      loading="lazy"
                      decoding="async"
                      className="w-24 h-24 object-cover rounded-xl border border-white/10"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-violet-400/80 mb-2">{rp.category}</div>
                    <div className="text-lg text-white group-hover:text-violet-300 transition-colors truncate">{rp.title}</div>
                    <div className="text-white/55 text-sm line-clamp-2 mt-1">{rp.description}</div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center detail-section"
        >
          <div className="p-12 border border-violet-500/20 rounded-2xl bg-gradient-to-br from-violet-900/10 to-transparent neon-border">
            <h3 className="text-3xl font-light mb-6">Interested in Similar Projects?</h3>
            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can bring your vision to life with the same level of excellence
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact" title="Start an AI creative project with Cultiv AI Studio">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 bg-white text-black px-10 py-5 rounded-xl text-sm tracking-wide hover:bg-white/90 transition-all duration-300"
                  style={{
                    boxShadow: '0 0 30px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  Start a project with Cultiv
                </motion.button>
              </Link>
              <Link to="/portfolio" title="See more AI viral case studies">
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center gap-3 border border-white/30 text-white px-10 py-5 rounded-xl text-sm tracking-wide hover:bg-white/5 transition-all duration-300"
                >
                  See more AI viral case studies
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default WorkDetail;
